if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          makerBadge: this.querySelector('.lyma-maker-badge'),
          fixedZoom: this.querySelector('.lyma-gallery-zoom-fixed'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (this.elements.viewer) this.elements.viewer.mediaGallery = this;
        this.updateMakerBadge();
        this.updateFixedZoom();

        this.elements.fixedZoom?.addEventListener('click', () => {
          const activeMedia = this.activeMedia || this.elements.viewer?.querySelector('[data-media-id].is-active');
          activeMedia?.querySelector('.product__media-toggle')?.click();
        });

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', (event) => {
            this.updateMakerBadge(event.detail.currentElement);
            this.updateFixedZoom(event.detail.currentElement);
          });
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      usesDiscreteMedia() {
        return this.mql.matches && this.dataset.desktopLayout.includes('thumbnail');
      }

      navigableMedia() {
        // Only the actual gallery slides are navigable. Each slide also
        // contains a zoom button with its own data-media-id; including those
        // nested controls created the blank extra step (slide -> zoom button
        // -> next slide).
        return [...this.elements.viewer.querySelectorAll('.product__media-list > .product__media-item[data-media-id]')].filter((item) =>
          !item.classList.contains('product__media-item--variant') || item === this.elements.viewer.querySelector('[data-media-id]'));
      }

      updateNavigation() {
        if (!this.usesDiscreteMedia()) return;
        const items = this.navigableMedia();
        const index = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
        const viewer = this.elements.viewer;
        if (viewer.prevButton) viewer.prevButton.disabled = index === 0;
        if (viewer.nextButton) viewer.nextButton.disabled = index >= items.length - 1;
      }

      moveMedia(direction) {
        const items = this.navigableMedia();
        const index = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
        const item = items[index + direction];
        if (item) this.setActiveMedia(item.dataset.mediaId, false);
      }

      connectedCallback() {
        this.updateNavigation();
        // Size only the main product gallery, never quick-add or mobile media.
        if (!this.closest('product-info[id^="MainProduct-"]')) return;
        this.fitDesktopQuery = window.matchMedia('(min-width: 990px)');
        this.scheduleDesktopFit = () => {
          cancelAnimationFrame(this.desktopFitFrame);
          this.desktopFitFrame = requestAnimationFrame(() => this.fitDesktopViewport());
        };
        this.desktopFitObserver = new ResizeObserver(this.scheduleDesktopFit);
        [this, this.parentElement, document.querySelector('.section-header'),
          document.querySelector('.announcement-bar-section')].filter(Boolean)
          .forEach((element) => this.desktopFitObserver.observe(element));
        window.addEventListener('resize', this.scheduleDesktopFit);
        window.addEventListener('load', this.scheduleDesktopFit);
        this.fitDesktopQuery.addEventListener('change', this.scheduleDesktopFit);
        document.fonts.ready.then(() => {
          if (this.isConnected) this.scheduleDesktopFit();
        });
        this.scheduleDesktopFit();
      }

      disconnectedCallback() {
        this.desktopFitObserver?.disconnect();
        cancelAnimationFrame(this.desktopFitFrame);
        window.removeEventListener('resize', this.scheduleDesktopFit);
        window.removeEventListener('load', this.scheduleDesktopFit);
        this.fitDesktopQuery?.removeEventListener('change', this.scheduleDesktopFit);
      }

      fitDesktopViewport() {
        if (!this.fitDesktopQuery.matches) {
          this.style.removeProperty('--lyma-desktop-gallery-width');
          this.style.removeProperty('--lyma-desktop-badge-scale');
          return;
        }
        const frame = this.querySelector('.lyma-product-main-media-frame');
        if (!frame || !frame.offsetWidth) return;
        const wrapper = this.parentElement;
        // The wrapper stays in document flow even when the gallery is sticky.
        // Its document top includes the actual header, breadcrumb and top gap.
        const top = wrapper.getBoundingClientRect().top + window.scrollY;
        const available = Math.max(1, window.innerHeight - top - 16);
        const frameHeight = frame.getBoundingClientRect().height;
        const controlsMargin = this.elements.thumbnails
          ? parseFloat(getComputedStyle(this.elements.thumbnails).marginBottom) || 0 : 0;
        const controlsHeight = Math.max(0, this.getBoundingClientRect().height - frameHeight) + Math.max(0, controlsMargin);
        const mediaRatio = frameHeight / frame.offsetWidth || 1;
        const width = Math.floor(Math.max(1, Math.min(wrapper.clientWidth * 0.8, (available - controlsHeight) / mediaRatio)));
        const previous = parseFloat(this.style.getPropertyValue('--lyma-desktop-gallery-width'));
        // Thumbnail height changes with gallery width. ResizeObserver converges
        // on the measured controls space without guessing a fixed pixel budget.
        if (!Number.isFinite(previous) || Math.abs(previous - width) > 1) {
          this.style.setProperty('--lyma-desktop-gallery-width', `${width}px`);
        }
        const badge = this.elements.makerBadge;
        if (badge) {
          const scale = Math.min(1, width / wrapper.clientWidth,
            Math.max(1, width - 32) / Math.max(1, badge.offsetWidth),
            Math.max(1, width - 32) / Math.max(1, badge.offsetHeight));
          this.style.setProperty('--lyma-desktop-badge-scale', scale);
        }
      }

      onSlideChanged(event) {
        if (!event.detail.currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
          element.classList.remove('scroll-trigger--cancel');
        });
        activeMedia?.classList?.add('is-active');
        // Slides hidden by the desktop thumbnail layout can remain marked as
        // offscreen by the scroll-reveal observer. Clear that state when a
        // slide becomes active so it cannot render as a blank transition.
        activeMedia?.classList?.remove('scroll-trigger--offscreen');
        activeMedia?.classList?.add('scroll-trigger--cancel');
        const activeImage = activeMedia?.querySelector('.product-media-container img');
        if (activeImage) {
          activeImage.loading = 'eager';
        }
        this.updateMakerBadge(activeMedia);
        this.updateFixedZoom(activeMedia);
        this.updateNavigation();

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      updateMakerBadge(activeMedia) {
        const badge = this.elements.makerBadge;
        const firstMedia = this.elements.viewer?.querySelector('[data-media-id]');
        if (!badge || !firstMedia) return;

        const currentMedia = activeMedia || this.elements.viewer.querySelector('[data-media-id].is-active') || firstMedia;
        const isFirstMedia = currentMedia?.dataset.mediaId === firstMedia.dataset.mediaId;
        badge.classList.toggle('is-hidden', !isFirstMedia);
        badge.setAttribute('aria-hidden', isFirstMedia ? 'false' : 'true');
      }

      updateFixedZoom(activeMedia) {
        const fixedZoom = this.elements.fixedZoom;
        const currentMedia = activeMedia || this.elements.viewer?.querySelector('[data-media-id].is-active');
        const toggle = currentMedia?.querySelector('.product__media-toggle');
        if (!fixedZoom || !toggle) return;

        this.activeMedia = currentMedia;
        fixedZoom.setAttribute('aria-label', toggle.querySelector('.visually-hidden')?.textContent?.trim() || fixedZoom.getAttribute('aria-label'));
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
